import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert, Loader2, CalendarDays, Users, Plane, StickyNote, CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { http, formatErr } from "@/lib/api";
import { ADDON_KEYS } from "@/components/site/BookingExtras";

const Row = ({ icon: Icon, label, value }) => (
  <li className="flex gap-3 text-sm"><Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#064E3B]" /><span className="w-28 shrink-0 text-slate-500">{label}</span><span className="font-medium text-slate-900">{value}</span></li>
);

export default function PartnerJob() {
  const { token } = useParams();
  const { t } = useLang();
  const [job, setJob] = useState(undefined);
  const [busy, setBusy] = useState(false);
  useEffect(() => { http.get(`/partner-jobs/${token}`).then((r) => setJob(r.data)).catch(() => setJob(null)); }, [token]);
  const setStatus = async (status) => {
    setBusy(true);
    try { await http.post(`/partner-jobs/${token}/status`, { status }); setJob({ ...job, status }); toast.success(t(`asg_${status}`)); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  if (job === undefined) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#064E3B]" /></div>;
  if (!job) return <p data-testid="job-not-found" className="mx-auto max-w-xl px-4 py-32 text-center text-slate-500">{t("job_not_found")}</p>;
  const cancelled = job.booking_status === "cancelled";
  return (
    <div data-testid="partner-job-page" className="mx-auto max-w-xl px-4 pb-32 pt-12 sm:pb-12">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/50 sm:p-8">
        <p className="font-mono text-xs text-slate-500">{job.code}</p>
        <h1 className="mt-1 text-2xl font-bold text-slate-900">{t("job_title")}</h1>
        <p className="mt-1 text-sm text-slate-500">{job.partner_name}</p>
        <ul className="mt-6 space-y-3">
          <Row icon={StickyNote} label={t("item")} value={job.item_name} />
          <Row icon={CalendarDays} label={t("date")} value={job.date} />
          <Row icon={Users} label={t("job_guest")} value={`${job.guest} · ${job.quantity}x`} />
          {job.transport_ref && <Row icon={Plane} label={t("transport_label")} value={job.transport_ref} />}
          {job.addons.length > 0 && <Row icon={CheckCircle2} label="Add-on" value={job.addons.map((a) => t(ADDON_KEYS[a])).join(", ")} />}
          {job.notes && <Row icon={StickyNote} label={t("job_notes")} value={job.notes} />}
        </ul>
        <p data-testid="job-status" className="mt-6 text-sm"><span className="text-slate-500">{t("job_status")}:</span> <b>{t(`asg_${job.status}`)}</b></p>
        {cancelled ? <p data-testid="job-cancelled" className="mt-4 rounded-2xl bg-slate-100 p-3 text-sm font-semibold text-slate-600">{t("job_cancelled")}</p> : (
          <div className="mt-4 flex gap-3">
            {job.status === "assigned" && <button disabled={busy} onClick={() => setStatus("accepted")} data-testid="job-accept-btn" className="flex-1 rounded-full bg-[#064E3B] py-3 font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60">{t("job_accept")}</button>}
            {job.status === "accepted" && <button disabled={busy} onClick={() => setStatus("done")} data-testid="job-done-btn" className="flex-1 rounded-full bg-[#E11D48] py-3 font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60">{t("job_done")}</button>}
          </div>
        )}
        <p data-testid="job-rules" className="mt-6 flex gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs leading-relaxed text-amber-900"><ShieldAlert className="h-4 w-4 shrink-0" />{t("job_rules")}</p>
      </div>
    </div>
  );
}
